import {
  AccessRuleEffect,
  AccessRuleScope,
  ALL_DOCUMENT_PERMISSION_FLAGS,
  DocumentPermissionFlag,
  type AccessRuleInput,
} from '@qualicloud/contracts';
import {
  isDocumentVisible,
  legacyMaxAggregate,
  resolveEffectivePermissions,
  type AccessRule,
  type AclSubject,
  type EffectivePermissions,
} from '@qualicloud/domain';

/**
 * Matriz de permissão efetiva — a peça que o legado não tinha.
 *
 * PROBLEMA QUE ISTO RESOLVE
 * O legado agregava as três escopos com `MAX()` por flag, o que é OU lógico
 * puro (ged.md §5.3): não havia negação, não havia precedência, e as flags eram
 * combinadas independentemente. O resultado era uma permissão efetiva que não
 * correspondia a nenhuma linha visível no formulário — o administrador
 * cadastrava as regras e não tinha como saber quem, no fim, podia o quê.
 *
 * O produto novo introduz DENY e precedência por especificidade
 * (USER > DEPARTMENT > ORGANIZATION, empate → DENY vence). Isso é mais
 * expressivo, mas também mais difícil de prever mentalmente. Por isso a tela
 * mostra o RESULTADO: para cada colaborador, as 6 flags finais e QUAL regra
 * produziu cada uma.
 *
 * INVARIANTE
 * O veredito (`allowed`) vem SEMPRE de `resolveEffectivePermissions`, a mesma
 * função pura que o CORE executa. A explicação (`winningRule`) é calculada em
 * separado e é puramente descritiva. Se as duas divergissem, a interface estaria
 * mentindo — há teste automatizado justamente para esse acoplamento.
 */

export interface SubjectDescriptor {
  readonly userId: string;
  readonly displayName: string;
  readonly jobTitle?: string;
  readonly departmentIds: readonly string[];
}

export interface FlagVerdict {
  readonly flag: DocumentPermissionFlag;
  readonly allowed: boolean;
  /** Índice da regra vencedora em `rules`. `null` = nenhuma regra menciona a flag. */
  readonly winningRuleIndex: number | null;
  readonly winningScope: AccessRuleScope | null;
  readonly winningEffect: AccessRuleEffect | null;
  /** Frase pronta em pt-BR explicando o porquê. */
  readonly explanation: string;
}

export interface SubjectMatrixRow {
  readonly subject: SubjectDescriptor;
  readonly verdicts: Readonly<Record<DocumentPermissionFlag, FlagVerdict>>;
  /** Documento aparece na Lista Mestra para este colaborador? */
  readonly visible: boolean;
  /**
   * `true` quando o resultado difere do que o legado produziria.
   * Serve para alertar durante a migração: a regra mudou de comportamento.
   */
  readonly divergesFromLegacy: boolean;
}

const SCOPE_SPECIFICITY: Record<AccessRuleScope, number> = {
  [AccessRuleScope.USER]: 3,
  [AccessRuleScope.DEPARTMENT]: 2,
  [AccessRuleScope.ORGANIZATION]: 1,
};

const SCOPE_NAME: Record<AccessRuleScope, string> = {
  [AccessRuleScope.USER]: 'colaborador',
  [AccessRuleScope.DEPARTMENT]: 'departamento',
  [AccessRuleScope.ORGANIZATION]: 'organização',
};

/** Converte a regra do formulário (schema Zod) para o formato do domínio. */
export function toDomainRule(rule: AccessRuleInput): AccessRule {
  return {
    scope: rule.scope as AccessRuleScope,
    effect: (rule.effect ?? AccessRuleEffect.ALLOW) as AccessRuleEffect,
    userId: rule.userId ?? null,
    departmentId: rule.departmentId ?? null,
    flags: rule.flags as DocumentPermissionFlag[],
  };
}

function ruleApplies(rule: AccessRule, subject: AclSubject): boolean {
  switch (rule.scope) {
    case AccessRuleScope.USER:
      return rule.userId === subject.userId;
    case AccessRuleScope.DEPARTMENT:
      return Boolean(rule.departmentId) && subject.departmentIds.includes(rule.departmentId as string);
    case AccessRuleScope.ORGANIZATION:
      return true;
    default:
      return false;
  }
}

/**
 * Determina qual regra decidiu cada flag.
 *
 * Reproduz a ORDEM de decisão de `resolveEffectivePermissions` (maior
 * especificidade vence; empate → DENY), mas apenas para saber QUEM decidiu.
 * O booleano final nunca sai daqui — sai do domínio.
 */
function explainFlag(
  rules: readonly AccessRule[],
  subject: AclSubject,
  flag: DocumentPermissionFlag,
  allowed: boolean,
): FlagVerdict {
  const applicable = rules
    .map((rule, index) => ({ rule, index }))
    .filter(({ rule }) => ruleApplies(rule, subject) && rule.flags.includes(flag));

  if (applicable.length === 0) {
    return {
      flag,
      allowed,
      winningRuleIndex: null,
      winningScope: null,
      winningEffect: null,
      explanation:
        'Nenhuma regra concede esta permissão. Sem regra explícita, o acesso é negado.',
    };
  }

  const maxSpecificity = Math.max(
    ...applicable.map(({ rule }) => SCOPE_SPECIFICITY[rule.scope]),
  );
  const contenders = applicable.filter(
    ({ rule }) => SCOPE_SPECIFICITY[rule.scope] === maxSpecificity,
  );

  // Empate de especificidade → DENY vence (fail-safe, igual ao domínio).
  const winner =
    contenders.find(({ rule }) => rule.effect === AccessRuleEffect.DENY) ?? contenders[0];

  if (!winner) {
    return {
      flag,
      allowed,
      winningRuleIndex: null,
      winningScope: null,
      winningEffect: null,
      explanation: 'Nenhuma regra aplicável.',
    };
  }

  const overridden = applicable.length - contenders.length;
  const scopeName = SCOPE_NAME[winner.rule.scope];
  const action = winner.rule.effect === AccessRuleEffect.DENY ? 'nega' : 'permite';

  let explanation = `A regra de escopo ${scopeName} ${action} esta permissão.`;
  if (overridden > 0) {
    explanation += ` Ela prevalece sobre ${overridden} ${
      overridden === 1 ? 'regra de escopo mais amplo' : 'regras de escopo mais amplo'
    }.`;
  }
  if (
    contenders.length > 1 &&
    winner.rule.effect === AccessRuleEffect.DENY
  ) {
    explanation += ' Em empate de escopo, a negação prevalece.';
  }

  return {
    flag,
    allowed,
    winningRuleIndex: winner.index,
    winningScope: winner.rule.scope,
    winningEffect: winner.rule.effect,
    explanation,
  };
}

/** Calcula a linha da matriz para um colaborador. */
export function buildSubjectRow(
  rules: readonly AccessRule[],
  subject: SubjectDescriptor,
): SubjectMatrixRow {
  const aclSubject: AclSubject = {
    userId: subject.userId,
    departmentIds: subject.departmentIds,
  };

  // Fonte da verdade: a MESMA função pura que o CORE executa.
  const effective: EffectivePermissions = resolveEffectivePermissions(rules, aclSubject);
  const legacy = legacyMaxAggregate(rules, aclSubject);

  const verdicts = Object.fromEntries(
    ALL_DOCUMENT_PERMISSION_FLAGS.map((flag) => [
      flag,
      explainFlag(rules, aclSubject, flag, effective[flag]),
    ]),
  ) as Record<DocumentPermissionFlag, FlagVerdict>;

  const divergesFromLegacy = ALL_DOCUMENT_PERMISSION_FLAGS.some(
    (flag) => effective[flag] !== legacy[flag],
  );

  return {
    subject,
    verdicts: Object.freeze(verdicts),
    visible: isDocumentVisible(effective),
    divergesFromLegacy,
  };
}

/** Matriz completa: uma linha por colaborador. */
export function buildPermissionMatrix(
  rules: readonly AccessRule[],
  subjects: readonly SubjectDescriptor[],
): SubjectMatrixRow[] {
  return subjects.map((subject) => buildSubjectRow(rules, subject));
}

export interface MatrixSummary {
  readonly totalSubjects: number;
  readonly visibleSubjects: number;
  /** Colaboradores sem NENHUMA das 6 flags — não veem o documento. */
  readonly invisibleSubjects: number;
  readonly perFlagCount: Readonly<Record<DocumentPermissionFlag, number>>;
  /** Nenhum aprovador designado torna ALL_REQUIRED e QUORUM inaplicáveis. */
  readonly hasApprover: boolean;
  readonly divergingSubjects: number;
}

export function summarizeMatrix(rows: readonly SubjectMatrixRow[]): MatrixSummary {
  const perFlagCount = Object.fromEntries(
    ALL_DOCUMENT_PERMISSION_FLAGS.map((flag) => [
      flag,
      rows.filter((row) => row.verdicts[flag].allowed).length,
    ]),
  ) as Record<DocumentPermissionFlag, number>;

  return {
    totalSubjects: rows.length,
    visibleSubjects: rows.filter((row) => row.visible).length,
    invisibleSubjects: rows.filter((row) => !row.visible).length,
    perFlagCount: Object.freeze(perFlagCount),
    hasApprover: perFlagCount[DocumentPermissionFlag.APPROVE] > 0,
    divergingSubjects: rows.filter((row) => row.divergesFromLegacy).length,
  };
}

/**
 * Avisos de configuração detectados na matriz.
 *
 * São problemas que o legado deixava passar em silêncio e só apareciam depois,
 * como documento que ninguém enxerga ou versão que nunca sai de "pendente".
 */
export function detectConfigurationWarnings(params: {
  readonly rows: readonly SubjectMatrixRow[];
  readonly approvalPolicy: string;
  readonly quorumSize: number | null;
}): string[] {
  const summary = summarizeMatrix(params.rows);
  const warnings: string[] = [];

  if (summary.totalSubjects > 0 && summary.visibleSubjects === 0) {
    warnings.push(
      'Nenhum colaborador enxerga este documento. Um documento sem nenhuma das 6 permissões fica invisível na Lista Mestra para todos.',
    );
  }

  if (!summary.hasApprover) {
    warnings.push(
      'Nenhum colaborador tem a permissão Aprovar. A versão ficará pendente de aprovação indefinidamente.',
    );
  }

  const approverCount = summary.perFlagCount[DocumentPermissionFlag.APPROVE];

  if (params.approvalPolicy === 'QUORUM' && params.quorumSize !== null) {
    if (approverCount < params.quorumSize) {
      warnings.push(
        `O quórum exige ${params.quorumSize} aprovações, mas apenas ${approverCount} ${
          approverCount === 1 ? 'colaborador tem' : 'colaboradores têm'
        } a permissão Aprovar. A versão nunca seria aprovada.`,
      );
    }
  }

  if (params.approvalPolicy === 'ALL_REQUIRED' && approverCount === 1) {
    warnings.push(
      'Com um único aprovador, a política "Todos os aprovadores" se comporta como "Qualquer aprovador". Considere designar mais aprovadores.',
    );
  }

  if (summary.divergingSubjects > 0) {
    /*
     * O que este aviso realmente detecta é uma REGRA DE NEGAÇÃO em ação: sem
     * ela, a permissão de cada pessoa seria a união de tudo que as regras
     * concedem. Dizer isso é útil — negação é a coisa mais fácil de configurar
     * sem perceber, e a pessoa precisa saber que alguém está sendo excluído de
     * propósito. Comparar com o sistema anterior, que era como a frase estava
     * escrita, não ajudava ninguém a decidir nada.
     */
    warnings.push(
      `A negação está removendo acesso de ${summary.divergingSubjects} ${
        summary.divergingSubjects === 1 ? 'colaborador' : 'colaboradores'
      }. Sem as regras de negação, ${
        summary.divergingSubjects === 1 ? 'ele teria' : 'eles teriam'
      } acesso pelas demais regras — confira se é o pretendido.`,
    );
  }

  return warnings;
}
